import { useGetPortfolios, useGetShares } from "@/modules/orders/application/hooks/useOrdersQueries";
import { ClientInstrument, ClientOrderEntity } from "../../domain/entities/ClientOrdersEntity";
import { enrichClientOrder } from "../../infrastructure/ClientOrderMapper";
import {
    useGetClientInstruments,
    useGetClientOrders,
    useGetClientRates,
} from "./useClientOrdersQueries";

import { useMemo } from "react";

type OptionItem = { id: number | string; name: string; detail?: string };

const mapToOptions = (data: OptionItem[] = []) => {
    return data.map(({ id, name, detail }) => ({
        detail,
        label: name,
        value: id.toString(),
    }));
}

export const useClientOrdersData = () => {
    const { data: clientOrdersData, isLoading: clientOrdersIsLoading, refetch: clientOrdersRefetch } = useGetClientOrders();
    const { data: clientInstrumentsData, isLoading: clientInstrumentsIsLoading } = useGetClientInstruments();
    const { data: portfoliosData, isLoading: portfoliosIsLoading } = useGetPortfolios();
    const { data: clientRatesData, isLoading: clientRatesIsLoading } = useGetClientRates();
    const { data: clientShareInstrumentsData, isLoading: clientShareInstrumentsIsLoading } = useGetShares();

    const portfolios = portfoliosData?.data || [];
    const clientShareInstruments = clientShareInstrumentsData?.data || [];
    const clientShareInstrumentsOptions = useMemo(
        () =>
            mapToOptions(
                clientShareInstruments.map((instrument) => ({
                    id: instrument.id,
                    name: instrument.mnemonic,
                    detail: `(${instrument.isin} - ${instrument.issuerName})`,
                }))
            ),
        [clientShareInstrumentsData]
    );

    const clientInstruments = clientInstrumentsData?.data || [];
    const clientInstrumentOptions = useMemo(
        () =>
            mapToOptions(
                clientInstruments.map((instrument) => ({
                    id: instrument.id,
                    name: instrument.mnemonic,
                    detail: `(${instrument.interestRate} - ${instrument.frequency} - ${instrument.issuanceDate} - ${instrument.maturityDate})`,
                }))
            ),
        [clientInstrumentsData]
    );
    const clientRates = clientRatesData?.data || [];

    const clientOrders: ClientOrderEntity[] = [];
    if (clientOrdersData?.data && (clientInstrumentsData?.data || clientShareInstrumentsData?.data)) {
        clientOrdersData.data.forEach((item) => {
            const {
                id,
                status,
                clientIdentification,
                clientIdentificationType,
                rentType,
                instrument,
                orderType,
                quantity,
                rate,
                value,
                createdAt,
                updatedAt,
                confirmNumber,
                limitPrice,
                limitType,
            } = item as any;

            // Try to resolve the instrument first from fixed-income instruments,
            // then from share instruments (mapping shares into a compatible shape).
            const fixedIncomeInstrument = clientInstruments.find((inst) => inst.id === instrument);
            const shareInstrument = clientShareInstruments.find((inst) => inst.id === instrument);

            const instrumentObj: ClientInstrument | undefined = fixedIncomeInstrument
                ? fixedIncomeInstrument
                : shareInstrument
                    ? {
                        id: shareInstrument.id,
                        mnemonic: shareInstrument.mnemonic,
                        instrumentType: "SHARE",
                        isin: shareInstrument.isin,
                        currency: "",
                        interestRate: "",
                        frequency: "",
                        issuanceDate: "",
                        maturityDate: "",
                        issuerName: shareInstrument.issuerName,
                        rating: "",
                        validation: "",
                    }
                    : undefined;

            if (instrumentObj) {
                // Create a mock client object from the identification data
                const mockClient = {
                    identificacion: clientIdentification,
                    tipoIdentificacion: clientIdentificationType,
                    nombre: clientIdentification, // Use ID as name for now
                    // Add other required Client fields with default values
                    codigoHomologado: "",
                    tipoCliente: "",
                    fideicomitente: "",
                    ciu_ced: "",
                    fechaExpedicion: "",
                    titulo: "",
                    autoretenedor: "",
                    declarante: "",
                    contribuyente: "",
                    cod_Ciiu: "",
                    fechaNacimiento: "",
                    sexo: "",
                    estadoCivil: "",
                    codigo_Ciudad: "",
                    cod_Empresa: 0,
                    relacion: "",
                    email: "",
                    especial: "",
                    caracteristicas: "",
                    celular: "",
                    transaccionesInternet: 0,
                    digito: "",
                    sipla: false,
                    fechaIngreso: "",
                    validacion: "",
                    fechaActulizacion: "",
                    ctaDeceval: "",
                    cupoJuridico: 0,
                    fechaTran: "",
                    ajuste: "",
                    primerNombre: "",
                    segundoNombre: "",
                    primerApellido: "",
                    segundoApellido: "",
                    bloqueado: false,
                    idBloqueo: 0,
                    red: 0,
                    activoSac: false,
                    excentoenDividendos: 0,
                    generaVar: false,
                    residenteColombia: false,
                    paisdeResidencia: 0,
                    ingresos: 0,
                    clasificacionCliente: 0,
                    vigilado: 0,
                    vigiladoSfc: false,
                    ficGpa: false,
                    exentoGmg: "",
                    address: []
                };

                const enrichedItem = enrichClientOrder(
                    {
                        status,
                        clientIdentification,
                        clientIdentificationType,
                        rentType,
                        orderType,
                        instrument,
                        quantity,
                        rate,
                        value,
                        confirmNumber,
                        // Preserve limit information coming from storage when present
                        limitPrice: limitPrice ?? 0,
                        limitType: limitType ?? "MERCADO",
                    },
                    id,
                    new Date(createdAt),
                    updatedAt ? new Date(updatedAt) : null,
                    {
                        clients: [mockClient],
                        instruments: [instrumentObj] as unknown as ClientInstrument[],
                    }
                );

                if (enrichedItem) {
                    clientOrders.push(enrichedItem);
                }
            }
        });
    }

    const isLoading =
        clientOrdersIsLoading ||
        clientInstrumentsIsLoading ||
        clientRatesIsLoading ||
        clientShareInstrumentsIsLoading ||
        portfoliosIsLoading;

    // Extract unique clients from orders for the form
    const clients = clientOrders.map(order => order.client);
    const clientOptions = clients.map(client => ({
        label: client.nombre || client.identificacion,
        value: client.identificacion
    }));

    return {
        clientOrders,
        clientInstruments,
        clientInstrumentOptions,
        clientRates,
        clients,
        clientOptions,
        clientShareInstruments,
        clientShareInstrumentsOptions,
        portfolios,
        isLoading,
        clientOrdersRefetch,
    };
}
